/**
 * SnapshotManager — Server-backed deal persistence.
 * All deals stored in MongoDB via /api/deals. localStorage is only cache/fallback.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { FloppyDisk, FolderOpen, Trash, PencilSimple, Clock, ArrowSquareOut, Archive, ArrowCounterClockwise } from '@phosphor-icons/react';
import { API_BASE_URL } from '@/config/api';
import { interpretDealAccessStatus } from '@/utils/openFileSession';
import { isAuthFailureStatus, notifyAuthExpired } from '@/utils/apiAuth';

const API = API_BASE_URL;
const SWIPE_THRESHOLD = 80;

function SwipeableRow({ children, onSwipe, actionLabel, actionColor, actionIcon }) {
  const rowRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const dragging = useRef(false);
  const [offset, setOffset] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [fired, setFired] = useState(false);

  const handleStart = useCallback((clientX) => {
    if (fired) return;
    startX.current = clientX;
    currentX.current = clientX;
    dragging.current = true;
    setTransitioning(false);
  }, [fired]);

  const handleMove = useCallback((clientX) => {
    if (!dragging.current || fired) return;
    currentX.current = clientX;
    const dx = currentX.current - startX.current;
    // Only allow left swipe, with resistance past threshold
    if (dx < 0) {
      const raw = Math.abs(dx);
      const clamped = raw > SWIPE_THRESHOLD ? SWIPE_THRESHOLD + (raw - SWIPE_THRESHOLD) * 0.3 : raw;
      setOffset(-clamped);
    } else {
      setOffset(0);
    }
  }, [fired]);

  const handleEnd = useCallback(() => {
    if (!dragging.current || fired) return;
    dragging.current = false;
    const dx = currentX.current - startX.current;
    if (Math.abs(dx) >= SWIPE_THRESHOLD) {
      // Fire action — slide out fully
      setTransitioning(true);
      setOffset(-300);
      setFired(true);
      setTimeout(() => onSwipe(), 250);
    } else {
      // Snap back
      setTransitioning(true);
      setOffset(0);
      setTimeout(() => setTransitioning(false), 200);
    }
  }, [onSwipe, fired]);

  // Touch events
  const onTouchStart = useCallback((e) => handleStart(e.touches[0].clientX), [handleStart]);
  const onTouchMove = useCallback((e) => handleMove(e.touches[0].clientX), [handleMove]);
  const onTouchEnd = useCallback(() => handleEnd(), [handleEnd]);

  // Mouse events
  const onMouseDown = useCallback((e) => { e.preventDefault(); handleStart(e.clientX); }, [handleStart]);
  useEffect(() => {
    if (!dragging.current) return;
    const onMM = (e) => handleMove(e.clientX);
    const onMU = () => handleEnd();
    window.addEventListener('mousemove', onMM);
    window.addEventListener('mouseup', onMU);
    return () => { window.removeEventListener('mousemove', onMM); window.removeEventListener('mouseup', onMU); };
  }); // intentionally no deps — re-registers each render while dragging

  const progress = Math.min(Math.abs(offset) / SWIPE_THRESHOLD, 1);
  const Icon = actionIcon;

  return (
    <div className="relative overflow-hidden" ref={rowRef} data-testid="swipeable-row">
      {/* Action background revealed on swipe */}
      <div className={`absolute inset-y-0 right-0 flex items-center justify-end px-4 ${actionColor}`}
        style={{ width: Math.max(Math.abs(offset), 60), opacity: progress }}>
        <div className="flex items-center gap-1 text-white">
          {Icon && <Icon weight="bold" className="w-4 h-4" />}
          <span className="text-[10px] font-bold whitespace-nowrap">{actionLabel}</span>
        </div>
      </div>
      {/* Sliding content */}
      <div
        style={{
          transform: `translateX(${offset}px)`,
          transition: transitioning ? 'transform 200ms ease-out' : 'none',
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        className="relative bg-white select-none"
      >
        {children}
      </div>
    </div>
  );
}

// ---- API helpers ----

export async function fetchDeals() {
  try {
    const res = await fetch(`${API}/api/deals`, { credentials: 'include' });
    if (!res.ok) return [];
    return await res.json();
  } catch { return []; }
}

export async function fetchDeal(id) {
  try {
    const res = await fetch(`${API}/api/deals/${id}`, { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

export async function checkDealAccess(id) {
  if (!id) return { status: 'missing', deal: null };
  try {
    const res = await fetch(`${API}/api/deals/${id}`, { credentials: 'include' });
    const status = interpretDealAccessStatus(res.status);
    if (status === 'missing') return { status, deal: null };
    if (status !== 'ok') return { status: 'unavailable', deal: null };
    const deal = await res.json();
    return { status: 'ok', deal };
  } catch {
    return { status: interpretDealAccessStatus(0, true), deal: null };
  }
}

function dealMutationFailure(status = 0) {
  return { data: null, status, authExpired: isAuthFailureStatus(status) };
}

function dealMutationSuccess(data, status = 200) {
  return { data, status, authExpired: false };
}

/**
 * POST /api/deals — returns { data, status, authExpired }.
 * Prefer this when callers need auth-aware error copy.
 */
export async function createDealDetailed(
  name,
  inputs,
  assumptions,
  notes = '',
  cadastreData = null,
  marketData = null,
  fxSnapshot = null,
  extra = {},
) {
  try {
    const { address, comment, locationData, ...cleanInputs } = inputs;
    const res = await fetch(`${API}/api/deals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        name,
        inputs: { ...cleanInputs, address: address || '', locationData: locationData || {} },
        assumptions,
        notes,
        cadastreData,
        marketData,
        fxSnapshot,
        ...(extra.scenarios != null ? { scenarios: extra.scenarios, activeScenarioId: extra.activeScenarioId || null } : {}),
        ...(extra.cashTracker !== undefined ? { cashTracker: extra.cashTracker } : {}),
        ...(extra.landAssessment !== undefined ? { landAssessment: extra.landAssessment } : {}),
        ...(extra.stageFinanceAssumptions !== undefined ? { stageFinanceAssumptions: extra.stageFinanceAssumptions } : {}),
      }),
    });
    if (!res.ok) {
      const failure = dealMutationFailure(res.status);
      if (failure.authExpired) notifyAuthExpired();
      return failure;
    }
    return dealMutationSuccess(await res.json(), res.status);
  } catch {
    return dealMutationFailure(0);
  }
}

export async function createDeal(
  name,
  inputs,
  assumptions,
  notes = '',
  cadastreData = null,
  marketData = null,
  fxSnapshot = null,
  extra = {},
) {
  const outcome = await createDealDetailed(
    name,
    inputs,
    assumptions,
    notes,
    cadastreData,
    marketData,
    fxSnapshot,
    extra,
  );
  return outcome.data;
}

/**
 * PUT /api/deals/:id — returns { data, status, authExpired }.
 * Prefer this when callers need auth-aware error copy.
 */
export async function updateDealDetailed(id, updates) {
  try {
    const body = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.inputs !== undefined) {
      const { comment, ...rest } = updates.inputs;
      body.inputs = rest;
    }
    if (updates.assumptions !== undefined) body.assumptions = updates.assumptions;
    if (updates.notes !== undefined) body.notes = updates.notes;
    if (updates.cadastreData !== undefined) body.cadastreData = updates.cadastreData;
    if (updates.marketData !== undefined) body.marketData = updates.marketData;
    if (updates.fxSnapshot !== undefined) body.fxSnapshot = updates.fxSnapshot;
    if (updates.scenarios !== undefined) body.scenarios = updates.scenarios;
    if (updates.activeScenarioId !== undefined) body.activeScenarioId = updates.activeScenarioId;
    if (updates.cashTracker !== undefined) body.cashTracker = updates.cashTracker;
    if (updates.landAssessment !== undefined) body.landAssessment = updates.landAssessment;
    if (updates.stageFinanceAssumptions !== undefined) body.stageFinanceAssumptions = updates.stageFinanceAssumptions;
    const res = await fetch(`${API}/api/deals/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const failure = dealMutationFailure(res.status);
      if (failure.authExpired) notifyAuthExpired();
      return failure;
    }
    return dealMutationSuccess(await res.json(), res.status);
  } catch {
    return dealMutationFailure(0);
  }
}

export async function updateDeal(id, updates) {
  const outcome = await updateDealDetailed(id, updates);
  return outcome.data;
}

export async function deleteDeal(id) {
  try {
    const res = await fetch(`${API}/api/deals/${id}`, { method: 'DELETE', credentials: 'include' });
    return res.ok;
  } catch { return false; }
}

export async function archiveDeal(id) {
  try {
    const res = await fetch(`${API}/api/deals/${id}/archive`, { method: 'PUT', credentials: 'include' });
    return res.ok;
  } catch { return false; }
}

export async function unarchiveDeal(id) {
  try {
    const res = await fetch(`${API}/api/deals/${id}/unarchive`, { method: 'PUT', credentials: 'include' });
    return res.ok;
  } catch { return false; }
}

export async function fetchSettings() {
  try {
    const res = await fetch(`${API}/api/settings`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.assumptions || null;
  } catch { return null; }
}

export async function saveSettings(assumptions) {
  try {
    await fetch(`${API}/api/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ assumptions }),
    });
  } catch { /* silent */ }
}

export async function migrateFromLocalStorage() {
  const SNAPSHOTS_KEY = 'redev-cockpit-snapshots';
  const SETTINGS_KEY = 'redev-cockpit-global-settings';
  const MIGRATED_KEY = 'redev-cockpit-migrated';

  // Skip if already migrated
  if (localStorage.getItem(MIGRATED_KEY)) return;

  try {
    const raw = localStorage.getItem(SNAPSHOTS_KEY);
    const settingsRaw = localStorage.getItem(SETTINGS_KEY);
    const localDeals = raw ? JSON.parse(raw) : [];
    const localSettings = settingsRaw ? JSON.parse(settingsRaw) : null;

    if (localDeals.length === 0 && !localSettings) {
      localStorage.setItem(MIGRATED_KEY, 'true');
      return;
    }

    // Push to backend
    const deals = localDeals.map(d => ({
      name: d.name || 'Untitled',
      inputs: d.inputs || {},
      assumptions: d.assumptions || {},
      notes: d.notes || d.inputs?.comment || '',
    }));

    const body = { deals };
    if (localSettings) body.settings = { assumptions: localSettings };

    const res = await fetch(`${API}/api/migrate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!res.ok) return; // Don't clear localStorage on failure

    const result = await res.json();

    // Verify: count matches
    if (result.migrated === deals.length) {
      localStorage.removeItem(SNAPSHOTS_KEY);
      localStorage.removeItem(SETTINGS_KEY);
      localStorage.setItem(MIGRATED_KEY, 'true');
    }
  } catch { /* silent — don't clear localStorage on error */ }
}

// ---- Utilities ----

const NOISE_WORDS = ['chemin', 'rue', 'route', 'avenue', 'boulevard', 'place', 'allée', 'allee', 'passage', 'impasse', 'sentier', 'de', 'du', 'des', 'la', 'le', 'les', 'l', 'au', 'aux', 'den', 'der', 'strasse', 'weg', 'gasse', 'platz'];

export function deriveNameFromAddress(inputs) {
  const addr = inputs?.address;
  if (!addr || addr.length < 3) return '';
  const postcodeMatch = addr.match(/\b(\d{4})\b/);
  const postcode = postcodeMatch ? postcodeMatch[1] : '';
  const firstPart = (addr.split(',')[0] || '').trim();
  const words = firstPart.split(/[\s'/.-]+/).filter(Boolean);
  const meaningful = words.filter(w => {
    if (/^\d+$/.test(w)) return false;
    return !NOISE_WORDS.includes(w.toLowerCase());
  });
  const streetWord = meaningful[0] || words[words.length - 1] || '';
  if (postcode && streetWord) return `${postcode} – ${streetWord}`;
  if (postcode) return postcode;
  if (streetWord) return streetWord;
  return firstPart.slice(0, 25);
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const verdictBadge = {
  'GO': 'bg-emerald-600 text-white',
  'WATCH': 'bg-amber-500 text-white',
  'NO GO': 'bg-red-600 text-white',
};

// ---- Components ----

/** Save-As Dialog — first save of a new draft, or explicit Duplicate. */
export function SaveAsDialog({ open, onOpenChange, inputs, onSave, mode = 'first' }) {
  const suggested = deriveNameFromAddress(inputs) || 'Untitled Analysis';
  const [name, setName] = useState(suggested);
  const [saving, setSaving] = useState(false);
  const isCopy = mode === 'copy';

  useEffect(() => {
    if (open) {
      const base = deriveNameFromAddress(inputs) || 'Untitled Analysis';
      setName(isCopy ? `${base} (copy)` : base);
    }
  }, [open, inputs, isCopy]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    let ok = true;
    try {
      // onSave may return false when the server write failed — keep dialog open.
      ok = await onSave(trimmed);
    } finally {
      setSaving(false);
    }
    if (ok !== false) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm p-4" data-testid="save-as-dialog" aria-describedby="save-as-desc">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold" data-testid="save-as-title">
            {isCopy ? 'Duplicate project' : 'Save project'}
          </DialogTitle>
          <p id="save-as-desc" className="text-[9px] text-gray-400" data-testid="save-as-desc">
            {isCopy
              ? 'Creates a separate copy. The original project is unchanged.'
              : 'Name this new draft. Saved to the server — accessible from any device.'}
          </p>
        </DialogHeader>
        <div className="mt-3 space-y-2">
          <label className="text-[10px] text-gray-500 font-medium">Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            className="h-7 text-[11px]"
            autoFocus
            data-testid="save-as-name-input"
          />
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-gray-100">
          <button onClick={() => onOpenChange(false)}
            className="text-[10px] text-gray-500 hover:text-gray-700 px-3 py-1 rounded hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="text-[10px] text-white bg-gray-900 hover:bg-gray-800 px-3 py-1 rounded font-medium flex items-center gap-1 disabled:opacity-50"
            data-testid="save-as-confirm-btn">
            <FloppyDisk weight="bold" className="w-3 h-3" />
            {saving ? (isCopy ? 'Duplicating…' : 'Saving...') : (isCopy ? 'Duplicate' : 'Save')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** File List Panel — browse and open server-stored deals */
export function FileListPanel({ open, onClose, onOpen, currentFileId }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchDeals().then(d => { setDeals(d); setLoading(false); });
    }
  }, [open]);

  const handleDelete = useCallback(async (id) => {
    const ok = await deleteDeal(id);
    if (ok) setDeals(prev => prev.filter(d => d.id !== id));
  }, []);

  const handleArchive = useCallback(async (id) => {
    const ok = await archiveDeal(id);
    if (ok) setDeals(prev => prev.map(d => d.id === id ? { ...d, archived: true } : d));
  }, []);

  const handleUnarchive = useCallback(async (id) => {
    const ok = await unarchiveDeal(id);
    if (ok) setDeals(prev => prev.map(d => d.id === id ? { ...d, archived: false } : d));
  }, []);

  const handleRename = useCallback(async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) { setEditingId(null); return; }
    const updated = await updateDeal(id, { name: trimmed });
    if (updated) setDeals(prev => prev.map(d => d.id === id ? { ...d, name: trimmed } : d));
    setEditingId(null);
  }, [editName]);

  const handleOpen = useCallback(async (deal) => {
    // Fetch full deal data (includes inputs + assumptions)
    const full = await fetchDeal(deal.id);
    if (full) {
      onOpen(full);
      onClose();
    }
  }, [onOpen, onClose]);

  if (!open) return null;

  const activeDeals = deals.filter(d => !d.archived);
  const archivedDeals = deals.filter(d => d.archived);
  const displayDeals = showArchived ? archivedDeals : activeDeals;
  const sorted = [...displayDeals].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

  return (
    <div className="fixed inset-0 z-[90] flex" data-testid="file-list-overlay">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative ml-auto w-full sm:w-[340px] bg-white h-full shadow-xl flex flex-col" data-testid="file-list-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <div className="flex items-center gap-1.5">
            <FolderOpen weight="bold" className="w-4 h-4 text-gray-600" />
            <span className="text-[12px] font-semibold text-gray-800">Projects</span>
            <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{activeDeals.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center gap-1 text-[9px] px-2 py-1 rounded transition-colors ${
                showArchived
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="toggle-archived-btn"
            >
              <Archive weight="bold" className="w-3 h-3" />
              {showArchived ? `Archived (${archivedDeals.length})` : `${archivedDeals.length} archived`}
            </button>
            <button onClick={onClose} className="text-[10px] text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100">
              Close
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && <div className="text-center py-12 text-[11px] text-gray-400">Loading...</div>}
          {!loading && sorted.length === 0 && (
            <div className="text-center py-12 text-[11px] text-gray-400">
              {showArchived ? 'No archived projects.' : 'No saved projects yet.'}
            </div>
          )}
          {sorted.map((deal) => {
            const isActive = deal.id === currentFileId;
            const isArchived = deal.archived;
            return (
              <SwipeableRow
                key={deal.id}
                onSwipe={() => isArchived ? handleUnarchive(deal.id) : handleArchive(deal.id)}
                actionLabel={isArchived ? 'Restore' : 'Archive'}
                actionColor={isArchived ? 'bg-emerald-500' : 'bg-amber-500'}
                actionIcon={isArchived ? ArrowCounterClockwise : Archive}
              >
              <div
                className={`border-b border-gray-100 px-3 py-1.5 hover:bg-gray-50 transition-colors group ${
                  isActive ? 'bg-blue-50/50 border-l-2 border-l-blue-400' :
                  isArchived ? 'opacity-60' : ''
                }`}
                data-testid={`file-item-${deal.id}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleOpen(deal)}
                    className="shrink-0 p-1 rounded bg-gray-100 hover:bg-blue-100 text-gray-500 hover:text-blue-600 transition-colors"
                    title="Open" data-testid={`file-open-${deal.id}`}>
                    <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                  </button>
                  <div className="min-w-0 flex-1">
                    {editingId === deal.id ? (
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                        onBlur={() => handleRename(deal.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRename(deal.id); if (e.key === 'Escape') setEditingId(null); }}
                        className="h-5 text-[11px] px-1" autoFocus data-testid={`file-rename-input-${deal.id}`} />
                    ) : (
                      <div className="text-[11px] font-medium text-gray-800 truncate cursor-pointer hover:text-blue-600"
                        onClick={() => handleOpen(deal)} data-testid={`file-load-${deal.id}`}>
                        {deal.name}
                        {isActive && <span className="text-[7px] text-blue-500 ml-1 font-bold">OPEN</span>}
                        {isArchived && <span className="text-[7px] text-amber-500 ml-1 font-bold">ARCHIVED</span>}
                        {deal.launched && <span className="text-[7px] text-emerald-600 ml-1 font-bold">CASH</span>}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 mt-px">
                      <div className="flex items-center gap-1 text-[8px] text-gray-400">
                        <Clock weight="regular" className="w-2.5 h-2.5" />
                        {formatDate(deal.updatedAt || deal.createdAt)}
                      </div>
                      {deal.roi != null && (
                        <span className="text-[8px] font-bold tabular-nums text-gray-600">{(deal.roi * 100).toFixed(0)}%</span>
                      )}
                      {deal.verdict && (
                        <span className={`text-[7px] font-bold px-1 py-px rounded leading-none ${verdictBadge[deal.verdict] || 'bg-gray-200 text-gray-600'}`}>{deal.verdict}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {!isArchived && (
                      <>
                        <button onClick={() => { setEditingId(deal.id); setEditName(deal.name); }}
                          className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600" title="Rename"
                          data-testid={`file-rename-btn-${deal.id}`}>
                          <PencilSimple weight="bold" className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleArchive(deal.id)}
                          className="p-1 rounded hover:bg-amber-50 text-gray-400 hover:text-amber-600" title="Archive"
                          data-testid={`file-archive-btn-${deal.id}`}>
                          <Archive weight="bold" className="w-3 h-3" />
                        </button>
                      </>
                    )}
                    {isArchived && (
                      <button onClick={() => handleUnarchive(deal.id)}
                        className="p-1 rounded hover:bg-emerald-50 text-gray-400 hover:text-emerald-600" title="Restore"
                        data-testid={`file-unarchive-btn-${deal.id}`}>
                        <ArrowCounterClockwise weight="bold" className="w-3 h-3" />
                      </button>
                    )}
                    <button onClick={() => handleDelete(deal.id)}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete permanently"
                      data-testid={`file-delete-btn-${deal.id}`}>
                      <Trash weight="bold" className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
              </SwipeableRow>
            );
          })}
        </div>
        {/* Swipe hint */}
        {!loading && sorted.length > 0 && (
          <div className="px-3 py-1.5 border-t border-gray-100 text-center">
            <span className="text-[8px] text-gray-300 italic">Swipe left to {showArchived ? 'restore' : 'archive'}</span>
          </div>
        )}
      </div>
    </div>
  );
}
